import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getObjectsById,
  objectsListFetch,
  deleteObjects,
} from "pages/AccessManagement/actions";
import { trimAndLimit } from "utils/general";
import ObjectViewModal from "./components/ObjectViewModal/ObjectViewModal";
import DeleteModal from "CommonComponent/DeleteModal";
import AccessRights from "utils/AccessRights";
import ObjectUpdate from "../update/ObjectUpdate";
import { DataTable } from "CommonComponent";

const toggleStatus = (is_active) => (
  <span className={is_active === "y" ? "status-Active" : "status-Inactive"}>
    {is_active === "y" ? "Active" : "Inactive"}
  </span>
);

const ObjectList = ({ ...rest }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rawObjectsState = useSelector((state) => state.objectsState || {});
  const objectsState = {
    objectsList: rawObjectsState.objectsList || [],
    showUpdateForm: rawObjectsState.showUpdateForm || false,
  };

  const session = useSelector((state) => state.session);
  const [viewModalShow, setViewModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [object_viewed_id, setObjectViewedId] = useState("");

  useEffect(() => {
    setObjectViewedId(session?.current_page_permissions?.object_id || "");
  }, [session]);

  useEffect(() => {
    const id = session?.current_page_permissions?.object_id || "";
    dispatch(objectsListFetch(id));
  }, [dispatch, session]);

  // --- Actions ---
  const getDesc = (val) => trimAndLimit(val, 30) || "";

  const renderTooltip = (description) => <Tooltip>{description}</Tooltip>;

  const showViewPopup = (recordId) => {
    setViewModalShow(true);
    dispatch(getObjectsById(recordId, "view"));
  };

  const showUpdateForm = (recordId) => {
    dispatch(getObjectsById(recordId, "update"));
    setShowEditModal(true);
  };

  const handleDelete = (recordId) => {
    setDeleteRowId(recordId);
    setDeleteModalShow(true);
  };

  // --- Columns for Common DataTable ---
  const columns = useMemo(
    () => [
      {
        id: "parent",
        header: "Parent",
        accessor: (row) => row.parent_objects?.name || "N/A",
        sortable: true,
        searchable: true,
      },
      {
        id: "name",
        header: "Name",
        accessor: (row) => row.name,
        sortable: true,
        searchable: true,
      },
      {
        id: "object_type",
        header: "Object Type",
        accessor: (row) => row.object_type?.opt_display || "N/A",
        sortable: true,
        searchable: true,
      },
      {
        id: "object_url",
        header: "URL",
        accessor: (row) => row.object_url,
        sortable: true,
        searchable: true,
      },
      {
        id: "menu_order",
        header: "Menu Order",
        accessor: (row) => row.menu_order,
        sortable: true,
        searchable: false,
      },
      {
        id: "status",
        header: "Status",
        accessor: (row) => toggleStatus(row.is_active),
        sortable: true,
        searchable: false,
      },
      {
        id: "description",
        header: "Description",
        accessor: (row) => (
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip(row.description)}
          >
            <span>{getDesc(row.description)}</span>
          </OverlayTrigger>
        ),
        sortable: false,
        searchable: true,
      },
      {
        id: "actions",
        header: "Actions",
        accessor: null,
        cell: (_, row) => (
          <div>
            <Image
              src="/assets/images/view-icon.svg"
              width={20}
              height={20}
              onClick={() => showViewPopup(row.id)}
              style={{ cursor: "pointer" }}
              className="me-2"
            />
            {AccessRights(
              session?.current_page_permissions,
              "edit",
              row.created_by
            ) &&
              row.is_deleted === "n" && (
                <Image
                  src="/assets/images/edit-icon.svg"
                  width={20}
                  height={20}
                  onClick={() => showUpdateForm(row.id)}
                  style={{ cursor: "pointer" }}
                  className="me-2"
                />
              )}
            <Image
              src="/assets/images/delete-icon.svg"
              width={20}
              height={20}
              onClick={() => handleDelete(row.id)}
              style={{ cursor: "pointer" }}
            />
          </div>
        ),
        sortable: false,
        searchable: false,
      },
    ],
    [session]
  );

  return (
    <div>
      <DataTable
        columns={columns}
        data={objectsState.objectsList}
        serverSide={false}
        showSearch={true}
        className="object-list-table"
      />
      <ObjectUpdate
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
      />
      <ObjectViewModal
        show={viewModalShow}
        onHide={() => setViewModalShow(false)}
      />
      <DeleteModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        onDelete={() => {
          dispatch(deleteObjects(deleteRowId, object_viewed_id));
          setDeleteModalShow(false);
          dispatch(objectsListFetch(object_viewed_id));
        }}
      />
    </div>
  );
};

export default ObjectList;
